// components/MoveFolderModal.tsx

import React, { useEffect, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { Modal } from "components/atoms/Modal/index";
import { fetchFoldersByUser } from "lib/actions"; // Ensure this function fetches all folders
import { FolderDTO } from "types";
import * as Styled from "./ActionFolderModal.styled";
interface ActionFolderModalProps {
  open: boolean;
  onClose: () => void;
  onAction: (folderId: string) => void;
  selectedFolderId: string | null;
  setSelectedFolderId: (folderId: string | null) => void;
  text: string;
}
const ActionFolderModal: React.FC<ActionFolderModalProps> = ({
  open,
  onClose,
  onAction,
  selectedFolderId,
  setSelectedFolderId,
  text
}) => {
  const [folders, setFolders] = useState<FolderDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchFoldersByUser().then(data => {
        setFolders(data as FolderDTO[] || []);
        setLoading(false);
      }).catch((e: Error) => {
        setError("Failed to fetch folders: " + e.message);
        setLoading(false);
      });
    }
  }, [open]);
  const handleAction = () => {
    if (!selectedFolderId) {
      setError("Please select a folder.");
      return;
    }
    onAction(selectedFolderId);
  };

  // Recursive function to render TreeItems
  const renderTree = (nodes: FolderDTO[]) => nodes.map(node => <TreeItem key={node.id} itemId={node.id} label={node.name}>
                {node.subFolders && node.subFolders.length > 0 ? renderTree(node.subFolders) : null}
            </TreeItem>);
  const handleSelect = (event: React.SyntheticEvent, itemId: string | null) => {
    setSelectedFolderId(itemId);
  };
  const filterFolders = (folders: FolderDTO[], query: string): FolderDTO[] => {
    return folders.map(folder => {
      if (folder.name.toLowerCase().includes(query.toLowerCase())) {
        return folder;
      }
      if (folder.subFolders) {
        const filteredSubFolders = filterFolders(folder.subFolders, query);
        if (filteredSubFolders.length > 0) {
          return {
            ...folder,
            subFolders: filteredSubFolders
          };
        }
      }
      return null;
    }).filter((folder): folder is FolderDTO => folder !== null);
  };
  const filteredFolders = searchTerm ? filterFolders(folders, searchTerm) : folders;
  return <Modal open={open} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-component="ActionFolderModal" data-sentry-source-file="index.tsx">
            <Styled.ModalContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.ModalTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">{text}</Styled.ModalTitle>
                <Styled.ModalText data-sentry-element="unknown" data-sentry-source-file="index.tsx">Select the target folder:</Styled.ModalText>
                <Styled.SearchTextField fullWidth label="Search Folders" variant="outlined" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                {loading ? <Styled.ModalText>Loading folders...</Styled.ModalText> : <SimpleTreeView aria-label="folder navigator" slots={{
        collapseIcon: ExpandMoreIcon,
        expandIcon: ChevronRightIcon
      }} onSelectedItemsChange={handleSelect} selectedItems={selectedFolderId} sx={{
        flexGrow: 1,
        overflowY: "auto",
        height: 400
      }}>
                        {renderTree(filteredFolders)}
                    </SimpleTreeView>}
                {error && <Styled.ModalText color="error">{error}</Styled.ModalText>}
                <Styled.ButtonContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Button onClick={onClose} variant={ButtonType.PRIMARY} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Cancel
                    </Button>
                    <Button onClick={handleAction} variant={ButtonType.SECONDARY} disabled={!selectedFolderId} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Confirm
                    </Button>
                </Styled.ButtonContainer>
            </Styled.ModalContainer>
        </Modal>;
};
export default ActionFolderModal;